import React, { useState } from 'react';
import { useFormik } from 'formik';
import { resetPassword } from 'aws-amplify/auth';
import { TextField, Box, Typography, Alert, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';

import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';
import { forgotPasswordSchema } from '../../validations/user';
import BorderedButton from '../common/buttons/BorderedButton';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';
import PrimaryUnderlinedLink from '../common/PrimaryUnderlinedLink';

const ForgotPassword: React.FC = () => {
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const md = useMediaQuery(theme.breakpoints.up('sm'));

  const handleOnSubmit = async (values: any) => {
    try {
      await resetPassword({ username: values.email });
      setResetSent(true);
      setError(null);
      navigate('/reset-password-confirmation');
    } catch (err) {
      let exc = '' + err;
      let errorMessage = null;
      if (exc.indexOf('NotAuthorizedException: User password cannot be reset in the current state.') >= 0) {
        errorMessage = 'Please activate your test kit and register your account.';
      } else {
        errorMessage = 'Failed to initiate password reset. Please try again.';
      }

      setError(errorMessage);
      console.error('Reset password error:', err);
    }
  };

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      email: '',
    },
    validationSchema: forgotPasswordSchema,
    onSubmit: handleOnSubmit,
  });

  if (resetSent) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: theme.custom.components.forgotPassword.typography.color }}>
          Password Reset Initiated
        </Typography>
        <Typography gutterBottom sx={{ color: theme.custom.components.forgotPassword.typography.color }}>
          If an account exists for {formik.values.email}, we've sent instructions to reset your password. Please check
          your email.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', py: 4, backgroundColor: theme.custom.components.forgotPassword.backgroundColor, borderRadius: '16px' }}>
      <Box
        sx={{
          flexBasis: { sm: '60%', md: '65%', xs: '100%' },
          padding: { xs: '0 15px', sm: '0 30px', md: '0 33px' },
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ marginBottom: 3, color: theme.custom.components.forgotPassword.typography.color }}
          >
            Reset your password
          </Typography>
          <Typography
            variant="body1"
            gutterBottom
            sx={{ color: theme.custom.components.forgotPassword.typography.color }}
          >
            Enter your user account's verified email address and we will send you a password reset code.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ width: { md: '80%', sx: '100%' } }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              autoComplete="email"
              autoFocus
              {...formik.getFieldProps('email')}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Box>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'center', sm: 'flex-start' },
              maxWidth: { xs: '100%', sm: 'auto' },
            }}
          >
            <PrimaryBlueButton
              isLoading={formik.isSubmitting}
              onClick={() => formik.handleSubmit()}
              buttonText="Reset Password"
              additionalProps={{
                disabled: !formik.isValid || formik.isSubmitting,
              }}
            />
            <BorderedButton
              sxProps={{
                height: '41px',
                marginLeft: { xs: '0', sm: '10px' },
                marginTop: { xs: '10px', sm: '0' },
              }}
              onClick={() => navigate('/')}
              buttonText={'Cancel'}
            />
          </Box>
        </Box>
        <Box sx={{ mt: 2, width: '100%' }}>
          <PrimaryUnderlinedLink navigateTo="/activate.html" text="Activate test kit and create your account" />
        </Box>
      </Box>
      {md && (
        <Box sx={{ maxWidth: '492px', maxHeight: '360px' }}>
          <img src="/images/login.png" alt="Login" style={{ maxWidth: '492px', maxHeight: '360px' }} />
        </Box>
      )}
    </Box>
  );
};

export default ForgotPassword;
